import React, { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { authTokenAtom } from 'atoms/authAtoms';
import { stageConfigAtom } from 'atoms/Ltool/stageConfigAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { gearIdAtom } from 'atoms/gearAtom';
import { errorAtom } from 'atoms/error';
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { useSubmitTargetGuard } from 'utils/hooks/useSubmitTargetGuard';
import { fetchAchievementConfig, fetchAchievementConfigHev } from 'api/fetchLtool';
import { submitAchievementLevelCalculation, submitAchievementLevelCalculationHev } from 'api/submitLtool';
import { PHENOMENON_IDS } from "utils/constsLtool";
import { PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { phenomenonPayloadAtom } from 'atoms/Ltool/newConfigAtoms';
import { calcTargetKeyAtom } from 'atoms/Ltool/calcTargetKeyAtom';
import { mappingAtom } from "atoms/mappingAtoms";
import { getMappingLabel } from "utils/mappingLabel";
import { toUserMessage } from "utils/apiErrorMessage";
import { WARNING_SUMMARY } from "utils/assessment";
import { buildLCalcConditionResult, type LCalcConditionResult } from "utils/lCalcConditionResult";
import {
    ACHIEVEMENT_GROUP_IDS,
    buildAchievementLevelRequest,
    createEmptyPayload,
    hasIncompleteGroupValues,
    isNegativeRiskMargin,
    toFormState,
    toNumberOrNull,
} from "utils/achievementConfigForm";
import { LCalcConditionResultAlerts } from "./LCalcConditionResultAlerts";

const REQUIRED_MESSAGE = '入力してください';

// 設定を取得して全欄に反映した対象（key）と、取得に成功したか。
type LoadedConfig = { key: string; ok: boolean };

export const AchievementLevelCalculation: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const setError = useSetAtom(errorAtom);
    const beginSubmit = useSubmitTargetGuard();
    const [payload, setPayload] = useAtom(phenomenonPayloadAtom);
    const mapping = useAtomValue(mappingAtom);

    const { showError, showWarning } = useSnackbar();
    // 送信中の対象 key の集合。対象ごとに管理し、古い対象の応答待ちの間も新しい対象の計算実行を押せるようにする
    // （同じ対象の二重送信は防ぐ）。単一の boolean だと、別の対象の送信完了で送信中の対象まで有効に戻ってしまう。
    const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());
    // 送信（検証失敗）後に、未入力の欄を欄単位でエラー表示する
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState<LCalcConditionResult | null>(null);
    const {
        project_id,
        destination_id,
        lot_id,
        phase_id,
        calculation_id,
    } = config;

    // HEV は gear 単位（freq は HEV 固有 phenomenon_id・選択 gear 分のみ送信）
    // 値域 2 種のユニオンだと .every の型ガード overload が解決できないため string[] に統一
    const isHev = powertrain === 'hev';
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    // 補正値・マージンは現象グループ単位（BEV: aco/vib/gn、HEV: gn/vib）
    const groupIds = ACHIEVEMENT_GROUP_IDS[isHev ? 'hev' : 'bev'];

    const configComplete = Boolean(
        project_id &&
        destination_id &&
        lot_id &&
        phase_id &&
        calculation_id
    );

    // HEV は gear 選択も前提
    const ready = configComplete && (!isHev || Boolean(gearId));

    // 設定の取得・送信の対象（計算セット / powertrain / gear）を表す key
    const targetKey = useAtomValue(calcTargetKeyAtom);
    const loading = inFlight.has(targetKey);

    // 取得中は render 中に同期で導出する。effect で立てる state だと、対象が変わった直後の
    // 1 render は前の対象の値のまま入力欄・計算実行が有効になり、誤った対象へ送信できてしまう。
    const [loaded, setLoaded] = useState<LoadedConfig | null>(null);
    const loadedRef = useRef<LoadedConfig | null>(null);
    const configLoading = ready && loaded?.key !== targetKey;

    // 登録済み（未登録ならデフォルト）の達成度設定を全欄の初期値として反映
    useEffect(() => {
        // トークン更新だけで再実行された場合（対象は取得成功済み）は、編集中の入力を消さず再取得もしない。
        // 取得に失敗していた場合はここを通らず、トークン更新を契機に再取得する。
        if (ready && loadedRef.current?.ok && loadedRef.current.key === targetKey) return;

        const settle = (next: LoadedConfig | null) => {
            loadedRef.current = next;
            setLoaded(next);
        };

        // 計算対象（計算セット / gear / powertrain）が変わったら前回のサマリ表示をクリアする
        // （再計算するまで別対象の結果が残って誤認するのを防ぐ）。
        setResult(null);
        setSubmitted(false);
        // 対象が変わった直後に前の対象の値が残らないよう、取得前に空へ戻す
        setPayload(createEmptyPayload());
        settle(null);

        if (!ready) return;

        let cancelled = false;

        const fetchDefaults = async () => {
            try {
                const configForFetch = {
                    project_id,
                    destination_id,
                    lot_id,
                    phase_id,
                    calculation_id,
                };
                const data = isHev
                    ? await fetchAchievementConfigHev(authToken, configForFetch, gearId!)
                    : await fetchAchievementConfig(authToken, configForFetch);
                if (cancelled) return;

                const form = toFormState(data, groupIds, phenomenonIds);
                setPayload(form);
                settle({ key: targetKey, ok: true });
                // BE は常に全グループ分の値を返す契約。欠落は旧形式の応答などの契約不整合の可能性がある
                if (hasIncompleteGroupValues(form, groupIds)) {
                    console.warn("達成度設定の取得結果に補正値・マージンの欠落があります（BE 契約との不整合の可能性）:", data);
                    showWarning("取得した達成度設定に補正値またはマージンの欠落があります。未入力の欄を入力してください。");
                }
            } catch (e) {
                if (cancelled) return;
                // 未登録でも 200 + デフォルト値が返るため、ここに来るのは実障害（認証・5xx・通信断）。
                // 空欄のまま黙らせず通知し、開発者向けの詳細は console.error に残す。
                console.error("達成度設定の取得に失敗しました:", e);
                showError(toUserMessage(e));
                settle({ key: targetKey, ok: false });
            }
        };

        fetchDefaults();

        return () => {
            cancelled = true;
        };
    }, [
        authToken,
        targetKey,
        project_id,
        destination_id,
        lot_id,
        phase_id,
        calculation_id,
        ready,
        isHev,
        gearId,
        groupIds,
        phenomenonIds,
        setPayload,
        showError,
        showWarning,
    ]);

    const handleGroupValueChange = (
        key: 'three_sigma' | 'risk_margin',
        groupId: string
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = toNumberOrNull(event.target.value);
        setPayload(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [groupId]: nextValue,
            },
        }));
    };

    const handleFreqChange = (
        phenomenonId: string,
        key: 'max' | 'min'
    ) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = toNumberOrNull(event.target.value);
        setPayload(prev => {
            const previous = prev.freq[phenomenonId] ?? { max: null, min: null };
            const updated = {
                ...previous,
                [key]: nextValue,
            };
            return {
                ...prev,
                freq: {
                    ...prev.freq,
                    [phenomenonId]: updated,
                },
            };
        });
    };

    const handleClick = async () => {
        // ガード（gear 未選択 / 未入力）で early return する場合も含め、まず前回のサマリ表示を消す。
        setResult(null);
        if (isHev && !gearId) {
            showWarning("HEV では Gear を選択してください。");
            return;
        }
        // BE の受け付け条件（全グループ入力済み・数値・マージンは 0 以上）を送信前に満たす。
        // L の計算画面には errorAtom の表示先が無いため、通知はスナックバーと欄単位のエラー表示で行う。
        const request = buildAchievementLevelRequest(payload, groupIds, phenomenonIds);
        if (!request.ok) {
            setSubmitted(true);
            showWarning(request.message);
            return;
        }
        // 送信中に計算セット / gear を切り替えた・L ツールを離れた場合、成功・失敗とも前の対象の応答を出さない。
        const guard = beginSubmit();
        setInFlight(prev => new Set(prev).add(guard.key));
        setError(null);
        try {
            // 原則 200 + 4 分類サマリ。成功・未登録・失敗を分類表示する。
            const summary = isHev
                ? await submitAchievementLevelCalculationHev(authToken, config, gearId!, request.body)
                : await submitAchievementLevelCalculation(authToken, config, request.body);
            if (guard.isStale()) return;
            setResult(buildLCalcConditionResult(summary, mapping));
        } catch (e) {
            // 画面には出さない古い対象の失敗でも、開発者向けの詳細は残す
            console.error("達成度の計算に失敗しました:", e);
            if (guard.isStale()) return;
            // 前提エラー（4xx/5xx）時のみ従来のエラー表示。
            // errorAtom は pathname 変化時にしか reset されず、画面を離れた後に書くと誰も消せずに残る
            // （L のセレクタが赤いままになる）ため、mount 中だけ書く。
            if (guard.isMounted()) setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            // 自分の対象の分だけ外す（別の対象の送信中の状態は消さない）
            setInFlight(prev => {
                const next = new Set(prev);
                next.delete(guard.key);
                return next;
            });
        }
    };

    // 送信を試みた後は、未入力の欄を欄単位でエラー表示する（入力すれば解除される）
    const isEmptyAfterSubmit = (value: number | null | undefined) =>
        submitted && (value ?? null) === null;

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                達成度の計算
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {`登録済みの目標値と設計値から条件ごとの達成度（OK / WARNING / NG）と未達周波数（Hz）を計算します。WARNING は、${WARNING_SUMMARY}です。`}
            </Typography>

            {configLoading && (
                <Typography variant="body2" color="text.secondary" role="status" sx={{ mb: 2 }}>
                    達成度設定を取得しています…
                </Typography>
            )}

            <Stack spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        現象グループごとの補正値・マージン（dB）
                    </Typography>
                    <Stack spacing={1}>
                        {groupIds.map(groupId => {
                            const groupLabel = getMappingLabel(mapping, 'condition_group_id', groupId);
                            const sigmaEmpty = isEmptyAfterSubmit(payload.three_sigma[groupId]);
                            const marginNegative = isNegativeRiskMargin(payload.risk_margin[groupId]);
                            const marginEmpty = isEmptyAfterSubmit(payload.risk_margin[groupId]);
                            return (
                                <Box
                                    key={groupId}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                        {groupLabel}
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            label={`補正値 three_sigma（${groupLabel}）`}
                                            type="number"
                                            value={payload.three_sigma[groupId] ?? ''}
                                            onChange={handleGroupValueChange('three_sigma', groupId)}
                                            disabled={configLoading}
                                            error={sigmaEmpty}
                                            helperText={sigmaEmpty ? REQUIRED_MESSAGE : '単位: dB（負値も可）'}
                                            fullWidth
                                        />
                                        <TextField
                                            label={`マージン risk_margin（${groupLabel}）`}
                                            type="number"
                                            value={payload.risk_margin[groupId] ?? ''}
                                            onChange={handleGroupValueChange('risk_margin', groupId)}
                                            disabled={configLoading}
                                            error={marginNegative || marginEmpty}
                                            helperText={
                                                marginNegative
                                                    ? '0 以上で入力してください'
                                                    : marginEmpty
                                                        ? REQUIRED_MESSAGE
                                                        : '単位: dB（0 以上）'
                                            }
                                            slotProps={{ htmlInput: { min: 0 } }}
                                            fullWidth
                                        />
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        現象IDごとの周波数範囲（Hz）
                    </Typography>
                    <Stack spacing={1}>
                        {phenomenonIds.map(phenomenonId => {
                            const freq = payload.freq[phenomenonId] ?? { max: null, min: null };
                            const minEmpty = isEmptyAfterSubmit(freq.min);
                            const maxEmpty = isEmptyAfterSubmit(freq.max);
                            return (
                                <Box
                                    key={phenomenonId}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                                        {getMappingLabel(mapping, 'phenomenon_id', phenomenonId)}
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                            label="最小値 (min)"
                                            type="number"
                                            value={freq.min ?? ''}
                                            onChange={handleFreqChange(phenomenonId, 'min')}
                                            disabled={configLoading}
                                            error={minEmpty}
                                            helperText={minEmpty ? REQUIRED_MESSAGE : undefined}
                                            fullWidth
                                        />
                                        <TextField
                                            label="最大値 (max)"
                                            type="number"
                                            value={freq.max ?? ''}
                                            onChange={handleFreqChange(phenomenonId, 'max')}
                                            disabled={configLoading}
                                            error={maxEmpty}
                                            helperText={maxEmpty ? REQUIRED_MESSAGE : undefined}
                                            fullWidth
                                        />
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Stack>

            <Button
                variant="contained"
                sx={{ minWidth: 140 }}
                disabled={loading || configLoading || (isHev && !gearId)}
                onClick={handleClick}
            >
                計算実行
            </Button>
            <LCalcConditionResultAlerts result={result} />
        </Box>
    );
};
